import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { _config } from '../Constants.js';
import { getCurrentUserLevel, updateConversationInFirestore, resetFirestoreDocId, getLastPlacementTest, saveUserTargetLanguage, getOrganizationChildrenMode, fetchUserPreferences } from '../utilities/firebase';
import { getUrlParameter } from '../utilities/urlUtils.js';
import { isTrialSession, markTrialSessionIfNeeded } from '../utilities/trialSession.js';
import { useCallback } from 'react';
import { updateLanguageVariant as updateLanguageVariantInFirebase } from '../utilities/firebase';
import { languageMap, syncLanguageChange } from '../utilities/i18next.js';
import { normalizeLevel, levelToQuebecScale } from '../utilities/levelManager.js';
import { shouldPromptPlacementTest as shouldPromptPlacementTestForState } from '../utilities/placementPrompt.js';
import { requestPlaybackStop } from '../utilities/playbackControl.js';

const SharedStateContext = createContext();
const DEFAULT_LANGUAGE_VARIANT = 'france';

export function useSharedState() {
  return useContext(SharedStateContext);
}

export function clearStorageValues() {
  localStorage.removeItem('hasAcknowledged');
  localStorage.removeItem('selectedActivity');
  localStorage.removeItem('userAge');
  localStorage.removeItem('userLanguage');
  localStorage.removeItem('userLevel');
  localStorage.removeItem('languageVariant');
  localStorage.removeItem('isRealtime');
  localStorage.removeItem('seenTutorial');
  localStorage.removeItem('echoCancellation');
  localStorage.removeItem('noiseSuppression');
  localStorage.removeItem('autoGainControl');
  localStorage.removeItem('ttsSpeed');
  localStorage.removeItem('preferredPronoun');
  localStorage.removeItem('i18nextLng');
  localStorage.removeItem('hideAssistantText');
  localStorage.removeItem('placementTestSkippedDate');
  localStorage.removeItem('placementTestAttemptedDate');
  localStorage.removeItem('useCustomAgent');
  localStorage.removeItem('sttModel'); // legacy key — the setting is 'audioModel' now
  localStorage.removeItem('audioModel');
  localStorage.removeItem('chatHistory');
  localStorage.removeItem('targetLanguage');
  localStorage.removeItem('trialSession');
  localStorage.removeItem('trialSessionCount'); // TRIAL_SESSION_COUNT_KEY in trialLimits.js
}

// Migration function to convert old language names to language codes
const migrateUserLanguage = () => {
  const storedLanguage = localStorage.getItem('userLanguage');
  if (storedLanguage) {
    // Check if it's a language name (old format) and convert to code
    if (languageMap[storedLanguage]) {
      const languageCode = languageMap[storedLanguage];
      console.log(`🔄 Migrating userLanguage from "${storedLanguage}" to "${languageCode}"`);
      localStorage.setItem('userLanguage', languageCode);
      return languageCode;
    }
    // If it's already a language code, return as is
    return storedLanguage;
  }
  return 'en'; // Default
};

export const SharedStateProvider = ({ children }) => {
  const [sharedState, setSharedState] = useState(() => {
    // Initialize state with values from localStorage or defaults

    const isRealtime = localStorage.getItem('isRealtime') !== null
      ? localStorage.getItem('isRealtime') === 'true'
      : false;
    // : isFrancoflex;

    // Get languageVariant from localStorage with proper fallback
    const storedLanguageVariant = localStorage.getItem('languageVariant');
    // Audio constraint settings
    const echoCancellation = localStorage.getItem('echoCancellation') !== null
      ? localStorage.getItem('echoCancellation') === 'true'
      : true;
    const noiseSuppression = localStorage.getItem('noiseSuppression') !== null
      ? localStorage.getItem('noiseSuppression') === 'true'
      : true;
    const autoGainControl = localStorage.getItem('autoGainControl') !== null
      ? localStorage.getItem('autoGainControl') === 'true'
      : true;
    // TTS playback speed (0.5, 0.75, 0.85, 1.0) - only affects non-realtime and ElevenLabs
    // Default to 0.85 for beginners (level 0-3) if user hasn't explicitly set a preference
    const storedTtsSpeed = localStorage.getItem('ttsSpeed');
    const userLevel = parseInt(localStorage.getItem('userLevel')) || 0;
    const ttsSpeed = storedTtsSpeed !== null
      ? parseFloat(storedTtsSpeed)
      : (userLevel <= 3 ? 0.85 : 1.0);

    // Preferred pronoun for inclusive language (prêt(e) → prêt, prête, or both)
    const preferredPronoun = localStorage.getItem('preferredPronoun') || 'both';

    // Hide assistant text in conversation view (audio-only mode)
    const hideAssistantText = localStorage.getItem('hideAssistantText') === 'true';

    // STT model selection (default or conversaflex)

    // console.log("Realtime: "+ isRealtime)
    let initialState = {

      // Core discussion options
      isRealtime: isRealtime, // Use realtime convo or one audio at a time.
      recording: false, // the audio is being recorded
      talking: false, // update Madame AI's state that it talking
      chatHistory: [], // The conversation in text
      conversationProgress: null, // Learning-path errors and acquired concepts for this attempt
      lastRecordedBlob: null, // Non-realtime, pass the audio
      finishRecording: false, // Stop everything, reset notification
      micMuted: false, // Mute/unmute microphone for realtime conversations

      // Realtime conversation status (ElevenLabs)
      conversationStatus: 'disconnected', // 'connecting' | 'connected' | 'disconnecting' | 'disconnected'
      conversationIsSpeaking: false, // Is the AI currently speaking in realtime mode

      // Audio constraints
      echoCancellation: echoCancellation,
      noiseSuppression: noiseSuppression,
      autoGainControl: autoGainControl,

      // TTS playback speed (0.75, 0.85, 1.0) - affects non-realtime and ElevenLabs
      ttsSpeed: ttsSpeed,

      // Preferred pronoun for inclusive language (prêt(e) → prêt, prête, or prêt ou prête)
      preferredPronoun: preferredPronoun,

      // Hide assistant text in conversation view (audio-only mode)
      hideAssistantText: hideAssistantText,

      // Core UI options
      loading: false, // Show a loader
      firstUserInput: false, // Clicked on Commencer, request the audio permissions
      showStartButton: true,
      showTour: false, // off by default, triggered when the showTour is setup
      
      // View management for right column
      currentView: 'conversation', // 'conversation', 'activities', 'planner'

      // Time limit
      startTime: null, // Timer to stop the free use of Madame AI
      timeLimit: null, // Time 

      // Activities timer
      timerId: null, // Timer for activities (ex: placement test)
      remainingTime: 0, // Add remainingTime to sharedState

      lastReminderTime: null, // Time elasped since the last reminder to continue or stop activity

      userAge: localStorage.getItem('userAge') || null,
      userLanguage: getUrlParameter('language') || migrateUserLanguage(), // URL param > localStorage
      userLevel: getUrlParameter('level')
        ? normalizeLevel(getUrlParameter('level'))
        : normalizeLevel(localStorage.getItem('userLevel')), // URL param > localStorage
      languageVariant: storedLanguageVariant || DEFAULT_LANGUAGE_VARIANT,
      selectedActivity: getUrlParameter('activity') || null, // URL param for partner pre-selection

      // Partner integration fields (URL-driven, session-scoped)
      partner: getUrlParameter('partner') || null,
      partnerUserId: getUrlParameter('uid') || null,
      partnerActivity: getUrlParameter('activity') || null, // Track if activity came from URL

      // Trial lead-gen session (exam-* / placement-test only). Sticky across in-iframe navigation.
      trial: isTrialSession(),

      // Target language to learn - will be loaded from Firestore in useEffect
      // Fallback chain: URL parameter → Firestore → 'fr' (default)
      targetLanguage: getUrlParameter('learn') || null, // URL parameter takes priority, Firestore will be fallback
      targetLanguageSource: getUrlParameter('learn') ? 'url' : null,

      // Organization ID for memory lookups - from URL parameter only (not persisted)
      organizationId: getUrlParameter('organizationId') || null,

      // Children mode - if org has childrenMode, audio recordings are not saved
      childrenMode: false,

      // Custom agent toggle (actual agent ID fetched from firebase.js getUserCustomBucket)
      useCustomAgent: localStorage.getItem('useCustomAgent') !== 'false', // Default to true

      // "Audio model" selection - 'default' (stock Gemini) or 'conversaflex'
      // (our fine-tune). null = the user never chose, in which case the client
      // sends no chatModel field and the server-side UNIFIED_AUDIO_MODEL
      // default applies (see cloud-run-ws-proxy/utils/llmStream.js).
      //
      // Replaces the old sttModel setting: our model used to be a separate
      // transcription step, and is the unified turn model now — one choice.
      audioModel: localStorage.getItem('audioModel') || null,

      // // placementTest
      shouldPromptPlacementTest: false,
      showTestResultsModal: false, // Show modal when any test completes (results sent via email)
    };

    localStorage.removeItem('selectedActivity'); // Start fresh each time you refresh.

    // Persist the trial-session flag so it survives in-iframe navigation that drops the ?trial param.
    markTrialSessionIfNeeded();

    // Load chat history from localStorage if configured
    if (_config.useLocalStorage) {
      const savedHistory = localStorage.getItem('chatHistory');
      initialState.chatHistory = savedHistory ? JSON.parse(savedHistory) : [];
    }

    return initialState;
  });

  // Sync userLanguage when changed externally (e.g., Firestore auth loads user's native language)
  useEffect(() => {
    const handler = (e) => {
      const language = e.detail?.language;
      if (language && language !== sharedState.userLanguage) {
        console.log('🌍 SharedState: Syncing userLanguage from external change:', language);
        setSharedState(prev => ({ ...prev, userLanguage: language }));
      }
    };
    window.addEventListener('userLanguageChanged', handler);
    return () => window.removeEventListener('userLanguageChanged', handler);
  }, [sharedState.userLanguage]);

  // Sync targetLanguage when changed externally (e.g., Firestore auth loads user's target language)
  useEffect(() => {
    const handler = (e) => {
      const lang = e.detail?.targetLanguage;
      if (lang && lang !== sharedState.targetLanguage) {
        console.log('🌍 SharedState: Syncing targetLanguage from Firestore:', lang);
        setSharedState(prev => ({ ...prev, targetLanguage: lang, targetLanguageSource: 'profile' }));
      }
    };
    window.addEventListener('targetLanguageChanged', handler);
    return () => window.removeEventListener('targetLanguageChanged', handler);
  }, [sharedState.targetLanguage]);

  // To sync languageVariant changes to localStorage
  useEffect(() => {
    if (sharedState.languageVariant) {
      localStorage.setItem('languageVariant', sharedState.languageVariant);
    }
  }, [sharedState.languageVariant]);

  // Hydrate persisted learner preferences from Firestore after auth has loaded.
  // The visible language variant should reflect the canonical user profile first,
  // not the historical Quebec fallback or a stale/missing localStorage value.
  useEffect(() => {
    let cancelled = false;

    const applyStoredPreferences = async () => {
      const prefs = await fetchUserPreferences();
      if (cancelled || !prefs) return;

      if (prefs.languageVariant) {
        localStorage.setItem('languageVariant', prefs.languageVariant);
        setSharedState(prevState => ({
          ...prevState,
          languageVariant: prefs.languageVariant,
        }));
      }

      if (prefs.targetLanguage && !getUrlParameter('learn')) {
        localStorage.setItem('targetLanguage', prefs.targetLanguage);
        setSharedState(prevState => ({
          ...prevState,
          targetLanguage: prefs.targetLanguage,
          targetLanguageSource: 'profile',
        }));
      }
    };

    applyStoredPreferences().catch(error => {
      console.warn('Unable to hydrate user preferences from Firestore:', error);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Initialize targetLanguage on mount
  // Fallback chain: URL parameter → localStorage → 'fr' (default)
  // Note: Firebase syncs targetLanguage to localStorage on auth, so no polling needed
  useEffect(() => {
    const urlTargetLanguage = getUrlParameter('learn');

    // Priority 1: URL parameter (?learn=en) - explicit partner/user intent
    if (urlTargetLanguage) {
      localStorage.setItem('targetLanguage', urlTargetLanguage);
      setSharedState(prevState => ({
        ...prevState,
        targetLanguage: urlTargetLanguage,
        targetLanguageSource: 'url'
      }));
      return;
    }

    // Priority 2: localStorage (synced from Firestore on auth, or set by onboarding)
    const localStorageLanguage = localStorage.getItem('targetLanguage');
    if (localStorageLanguage) {
      console.log('🌍 Target language from localStorage:', localStorageLanguage);
      setSharedState(prevState => ({
        ...prevState,
        targetLanguage: localStorageLanguage,
        targetLanguageSource: 'storage'
      }));
      return;
    }

    // Priority 3: Default to French
    console.log('🌍 Target language defaulting to: fr (French)');
    setSharedState(prevState => ({
      ...prevState,
      targetLanguage: 'fr',
      targetLanguageSource: 'default'
    }));
  }, []); // Run once on mount

  // Persist only explicit URL target-language intent from this shared-state layer.
  // Profile hydration, onboarding, and settings have their own save paths. Saving
  // the default ('fr') or stale localStorage during startup can overwrite the
  // learner's canonical preference before hydration completes.
  useEffect(() => {
    if (sharedState.targetLanguage && sharedState.targetLanguageSource === 'url') {
      saveUserTargetLanguage(sharedState.targetLanguage).catch(error => {
        console.error('Failed to save targetLanguage to Firestore:', error);
      });
    }
  }, [sharedState.targetLanguage, sharedState.targetLanguageSource]);

  // Fetch organization childrenMode if organizationId is present
  useEffect(() => {
    const fetchChildrenMode = async () => {
      if (sharedState.organizationId) {
        const childrenMode = await getOrganizationChildrenMode(sharedState.organizationId);
        if (childrenMode) {
          // Disable realtime mode for childrenMode organizations
          setSharedState(prevState => ({ ...prevState, childrenMode: true, isRealtime: false }));
          localStorage.setItem('isRealtime', 'false');
        }
      }
    };
    fetchChildrenMode();
  }, [sharedState.organizationId]);

  // To update Language variant properly
  const updateLanguageVariant = useCallback((newVariant) => {
    
    setSharedState(prevState => ({
      ...prevState,
      languageVariant: newVariant
    }));
    
    // To update localStorage immediately
    localStorage.setItem('languageVariant', newVariant);
    
    // Update Firebase preferences
    updateLanguageVariantInFirebase(newVariant).catch(error => {
      console.error('🇫 Failed to save Language variant to Firebase:', error);
    });
  }, []);

  // Update default ttsSpeed when userLevel changes (only if user hasn't explicitly set a speed)
  useEffect(() => {
    const storedTtsSpeed = localStorage.getItem('ttsSpeed');
    // Only auto-update speed if user hasn't explicitly set a preference
    if (storedTtsSpeed === null && sharedState.userLevel) {
      const level = parseInt(sharedState.userLevel) || 0;
      const defaultSpeed = level <= 3 ? 0.85 : 1.0;
      if (sharedState.ttsSpeed !== defaultSpeed) {
        console.log(`🔊 Auto-setting ttsSpeed to ${defaultSpeed} for level ${level}`);
        setSharedState(prevState => ({
          ...prevState,
          ttsSpeed: defaultSpeed
        }));
      }
    }
  }, [sharedState.userLevel]);

  useEffect(() => {
    // Sync chat history with localStorage if configured
    if (_config.useLocalStorage) {
      localStorage.setItem('chatHistory', JSON.stringify(sharedState.chatHistory));
    }

    // Sync chat history with remote storage if configured
    if (_config.useRemoteStorage && sharedState.chatHistory.length > 0) {
      updateConversationInFirestore(sharedState.chatHistory);
    }

    // Sync other state properties with localStorage
    if (sharedState.userAge) localStorage.setItem('userAge', sharedState.userAge);
    if (sharedState.userLanguage) localStorage.setItem('userLanguage', sharedState.userLanguage);
    if (sharedState.userLevel) localStorage.setItem('userLevel', sharedState.userLevel);
    if (sharedState.selectedActivity) {
      console.log("selectedActivity updated " + sharedState.selectedActivity);
    }
    if (sharedState.selectedActivity) localStorage.setItem('selectedActivity', sharedState.selectedActivity);
  }, [
    sharedState.chatHistory,
    sharedState.userAge,
    sharedState.userLanguage,
    sharedState.userLevel,
    sharedState.selectedActivity,
    _config.useLocalStorage
  ]);

  // User level initialization moved to firebase.js alongside other user data
  // See: firebase.js onAuthStateChanged -> fetchCurrentUserLevel()

  if (process.env.NODE_ENV === 'development') {
    window.__setSharedState = setSharedState
    window.__getSharedState = () => sharedState
    window.addChatMessage = function (role, content) {
      setSharedState(prev => ({
        ...prev,
        chatHistory: [
          ...prev.chatHistory,
          { role, content }
        ]
      }))
    }

    // // Add a user message
    // addChatMessage('user', 'This is a user message!')

    // // Add an assistant message
    // addChatMessage('assistant', 'This is an assistant reply!')
  }

  //** STATE SELECTORS */

  const updateSelectedActivity = (activityValue, showStartButton = false) => {
    requestPlaybackStop('activity-selection');

    // Reset firestoreDocId and chatHistory when activity changes to ensure new conversation
    resetFirestoreDocId();
    
    setSharedState((prevState) => {
      const newState = {
        ...prevState,
        selectedActivity: activityValue,
        showActivities: false,
        showStartButton: showStartButton,
        chatHistory: [], // Clear chat history for new activity
      };

      // // Only prompt if we're actually selecting an activity
      if (activityValue !== 'tutorial' && activityValue !== 'placement-test') {
        promptPlacementTestIfNeeded(newState); // Pass the new state
      } else {
        newState.shouldPromptPlacementTest = false; // Always ensure it's false if no activity
      }
      return newState;
    });

    if (activityValue) {
      localStorage.setItem('selectedActivity', activityValue)
    } else {
      localStorage.removeItem('selectedActivity')
    }
  }

  //** PLACEMENT TEST */

  const getLatestLevel = () => {
    // Check cached user level from firebase.js (initialized on auth)
    const cachedLevel = getCurrentUserLevel();
    if (cachedLevel) {
      if (cachedLevel !== parseInt(sharedState.userLevel)) {
        // Make sure the selectedLevel becomes the level from firebase cache if present
        setSharedState((prevState) => ({
          ...prevState,
          userLevel: cachedLevel
        }))
      }
      localStorage.setItem('userLevel', cachedLevel); // make sure we store a number

      return cachedLevel;
    } else if (sharedState.userLevel) {
      const normalized = levelToQuebecScale(sharedState.userLevel);
      if (normalized) {
        localStorage.setItem('userLevel', normalized); // make sure we store a number
        return normalized;
      }
    } else if (localStorage.getItem('userLevel') !== null) {
      const storedLevel = localStorage.getItem('userLevel');
      // Skip invalid stored values like 'unknown' or 'null'
      if (storedLevel && storedLevel !== 'unknown' && storedLevel !== 'null') {
        const quebecScaleLevel = levelToQuebecScale(storedLevel);
        if (quebecScaleLevel) {
          localStorage.setItem('userLevel', quebecScaleLevel); // make sure we store a number
          return quebecScaleLevel;
        }
      }
    }

    // Return null if no level set - caller should use ?? 3 for backend API calls
    return null;
  }

  const promptPlacementTestIfNeeded = (newState, options = {}) => {
    const hasCompletedOnboarding = options.hasCompletedOnboarding ?? localStorage.getItem('hasCompletedOnboarding') === 'true';
    const hasUrlOverride = Boolean(getUrlParameter('shouldPromptPlacementTest'));
    const placementTestStartedThisSession = sessionStorage.getItem('placementTestStartedThisSession') === 'true';
    const lastSkippedAt = localStorage.getItem('placementTestSkippedDate');
    const lastAttemptedAt = localStorage.getItem('placementTestAttemptedDate');
    const currentUserLevel = getCurrentUserLevel();
    const lastPlacementTest = getLastPlacementTest();
    const nativeLanguage = options.nativeLanguage ?? localStorage.getItem('userLanguage') ?? sharedState.userLanguage;
    const targetLanguage = options.targetLanguage ?? localStorage.getItem('targetLanguage') ?? sharedState.targetLanguage;

    const shouldPrompt = shouldPromptPlacementTestForState({
      hasCompletedOnboarding,
      hasUrlOverride,
      placementTestStartedThisSession,
      lastSkippedAt,
      lastAttemptedAt,
      currentUserLevel,
      lastPlacementTest,
      nativeLanguage,
      targetLanguage,
      hasSeenTutorial: localStorage.getItem('seenTutorial') === 'true',
      hasAcknowledged: localStorage.getItem('hasAcknowledged') === 'true',
    });

    if (placementTestStartedThisSession && !hasUrlOverride) {
      console.log('User already started placement test this session, not re-prompting');
    } else if (lastSkippedAt && !shouldPrompt && !hasUrlOverride) {
      console.log('User skipped placement test recently, not prompting');
    } else if (lastAttemptedAt && !shouldPrompt && !hasUrlOverride) {
      console.log('User attempted placement test recently, not prompting');
    } else if (currentUserLevel && lastPlacementTest?.date && shouldPrompt) {
      console.log('Last placement test is 6+ months old, prompting for re-test');
    } else if (currentUserLevel && !lastPlacementTest?.date && shouldPrompt) {
      console.log('User has a level but no placement test date, prompting for placement test');
    }

    if (typeof newState === 'object' && newState !== null) {
      newState.shouldPromptPlacementTest = shouldPrompt;
    } else {
      setSharedState(prevState => ({
        ...prevState,
        shouldPromptPlacementTest: shouldPrompt,
      }));
    }

    return shouldPrompt;
  };

  // UI update when an activity is selected
  const handleActivities = () => {
    requestPlaybackStop('activities-view');

    setSharedState(prevState => ({
      ...prevState,
      showStartButton: false,
      currentView: 'activities',
      showActivities: true // Ensure activities are visible in both realtime and non-realtime modes
    }));
  };

  // Switch to planner view
  const switchToPlannerView = () => {
    requestPlaybackStop('planner-view');

    setSharedState(prevState => ({
      ...prevState,
      currentView: 'planner'
    }));
  };

  // Switch to conversation view
  const switchToConversationView = () => {
    requestPlaybackStop('conversation-view');

    setSharedState(prevState => ({
      ...prevState,
      currentView: 'conversation'
    }));
  };

  // Switch to activities view
  const switchToActivitiesView = () => {
    requestPlaybackStop('activities-view');

    setSharedState(prevState => ({
      ...prevState,
      currentView: 'activities',
      showStartButton: false,
      showActivities: true // Ensure activities are visible in both realtime and non-realtime modes
    }));
  };

  // Conversation start handler - to be set by conversation components
  // const [conversationStartHandler, setConversationStartHandler] = useState(null);
	const conversationStartHandlerRef = useRef(null);

  const setConversationStartHandler = (handler) => {
    // console.log('[SharedStateContext] setConversationStartHandler called with', handler)
    conversationStartHandlerRef.current = handler
  }
  const startConversation = useCallback(async (activity) => {
    requestPlaybackStop('conversation-start');

    // console.log('[SharedStateContext] startConversation called, handler is', conversationStartHandlerRef.current)
    if (conversationStartHandlerRef.current) {
      return await conversationStartHandlerRef.current(activity)
    } else {
      console.warn('[SharedStateContext] No conversation start handler registered')
    }
  }, [])

  // User sees the app for the first time.
  const handleAcknowledge = (age, language, level, showTutorial, languageVariant = DEFAULT_LANGUAGE_VARIANT) => {
    localStorage.setItem('hasAcknowledged', 'true');
    localStorage.setItem('seenTutorial', showTutorial);
    localStorage.setItem('userAge', age);
    localStorage.setItem('userLevel', level);
    localStorage.setItem('languageVariant', languageVariant);

    // Use synchronized language change to keep userLanguage and i18nextLng in sync
    syncLanguageChange(language);

    setSharedState(prevState => ({
      ...prevState,
      userAge: age,
      userLanguage: language,
      userLevel: level,
      languageVariant: languageVariant,
      ...(showTutorial ? {
        isRealtime: false, // Do not do realtime for the onboarding convo
        selectedActivity: "tutorial",
        showActivities: false,
        showStartButton: true,
        showTour: true // trigger the UI tour
      } : {}),
    }));
  };


  return (
    <SharedStateContext.Provider value={{
      sharedState,
      setSharedState,
      handleAcknowledge,
      handleActivities,
      updateSelectedActivity,
      getLatestLevel,
      promptPlacementTestIfNeeded,
      updateLanguageVariant,
      switchToPlannerView,
      switchToConversationView,
      switchToActivitiesView,
      startConversation,
      setConversationStartHandler,
    }}>
      {children}
    </SharedStateContext.Provider>
  );
};
